import './appointment-exception-list-item.scss';

import { kebabCase } from 'lodash';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import {
  AgendaAppointmentItemDataFragment,
  ConsultType,
} from '../../data/types/api/graphql';
import { formatDateTimeToTime } from '../../data/utils/date.utils';
import { useFormatters } from '../../data/utils/formatters.utils';
import { makeKeyDownHandler } from '../../data/utils/make-key-down-handler';
import { CustomFormattedDate } from '../intl/custom-formatted-date';
import { CustomFormattedMessage } from '../intl/custom-formatted-message';

export enum AppointmentExceptionListItemType {
  DEFAULT = 'DEFAULT',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
}

type AppointmentExceptionListItemProps = {
  type?: AppointmentExceptionListItemType;
  appointmentItem: AgendaAppointmentItemDataFragment;
  includePatient?: boolean;
  includeHealthProfessional?: boolean;
  onClick?: (value: AgendaAppointmentItemDataFragment) => void;
};

export function AppointmentExceptionListItem({
  type = AppointmentExceptionListItemType.DEFAULT,
  appointmentItem,
  includePatient = false,
  includeHealthProfessional = false,
  onClick,
}: AppointmentExceptionListItemProps) {
  const intl = useIntl();

  const onHandleClick = useCallback(() => {
    onClick?.(appointmentItem);
  }, [appointmentItem, onClick]);

  const { getFullName } = useFormatters();

  return (
    <div
      className={`appointment-exceptions-list-item appointment-exceptions-list-item--${kebabCase(
        type
      )} appointment-exceptions-list-item--${kebabCase(
        appointmentItem.consultType
      )}`}
      role="button"
      tabIndex={0}
      onClick={onHandleClick}
      onKeyDown={makeKeyDownHandler(onHandleClick)}
    >
      <div className="appointment-exceptions-list-item__time">
        <CustomFormattedDate value={appointmentItem.startTime} />

        <div className="appointment-exceptions-list__item__date-info__time">
          {`${formatDateTimeToTime(
            appointmentItem.startTime,
            intl
          )} - ${formatDateTimeToTime(appointmentItem.endTime, intl)}`}
        </div>
      </div>

      <div className="appointment-exceptions-list-item__location">
        <p>
          <i
            className={`icon ${
              appointmentItem.consultType === ConsultType.Else
                ? 'clock'
                : kebabCase(appointmentItem.consultType)
            }`}
          />
          <CustomFormattedMessage
            id={`common.agenda-appointment-type.${kebabCase(
              appointmentItem.consultType
            )}`}
          />
        </p>
      </div>

      <div className="appointment-exceptions-list-item__content">
        {includePatient && <h3>{getFullName(appointmentItem.patient)}</h3>}

        {includeHealthProfessional && (
          <h3>{getFullName(appointmentItem.healthProfessional)}</h3>
        )}

        {appointmentItem.title && <p>{appointmentItem.title}</p>}
      </div>
    </div>
  );
}
